import React, { useEffect, useState, useMemo } from 'react';
import { Button, Layout, Modal, Space, Table, Tag, Spin, message, Form, Input, InputNumber, DatePicker, Drawer, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import ctcService from '../services/ctcService';
import employeeService from '../services/employeeService';
import organizationService from '../services/organizationService';
import { fetchCTCs, createCTC, updateCTC, deleteCTC } from '../redux/slices/ctcSlice';
import { canManageCTC, isSuperAdmin } from '../utils/permissions';

function CTC() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { list: ctcs, loading, pagination } = useSelector((state) => state.ctc);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCTC, setSelectedCTC] = useState(null);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(user?.organizationId || undefined);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId),
    [selectedOrganizationId, user]
  );

  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      }
    };

    loadOrganizations();
  }, [user]);

  useEffect(() => {
    loadCTCs();
    loadEmployees();
  }, [effectiveOrganizationId]);

  const loadCTCs = async (page = 1, limit = 10) => {
    setTableLoading(true);
    try {
      await dispatch(fetchCTCs({ page, limit, filters: { organizationId: effectiveOrganizationId } })).unwrap();
    } catch (error) {
      message.error(error || 'Failed to load CTCs');
    } finally {
      setTableLoading(false);
    }
  };

  const loadEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const response = await employeeService.getAll(1, 1000, { organizationId: effectiveOrganizationId });
      const employeeList = response?.data?.data || response?.data || [];
      setEmployees(employeeList);
    } catch (error) {
      message.error('Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleCreateCTC = () => {
    setIsEditMode(false);
    setSelectedCTC(null);
    form.resetFields();
    form.setFieldsValue({
      organizationId: effectiveOrganizationId
    });
    setIsModalOpen(true);
  };

  const handleEditCTC = (ctc) => {
    setIsEditMode(true);
    setSelectedCTC(ctc);
    form.setFieldsValue({
      organizationId: ctc.organizationId,
      employeeId: ctc.employeeId,
      ctcAmount: ctc.ctcAmount,
      effectFrom: dayjs(ctc.effectFrom),
      effectTo: ctc.effectTo ? dayjs(ctc.effectTo) : null,
      isActive: ctc.isActive
    });
    setIsModalOpen(true);
  };

  const handleViewCTC = (ctc) => {
    setViewData(ctc);
    setViewDrawerOpen(true);
  };

  const handleSaveCTC = async (values) => {
    try {
      const payload = {
        ...values,
        effectFrom: values.effectFrom.toISOString(),
        effectTo: values.effectTo ? values.effectTo.toISOString() : null,
        organizationId: isSuperAdmin(user) ? values.organizationId : effectiveOrganizationId
      };

      if (isEditMode && selectedCTC?.id) {
        await dispatch(updateCTC({ id: selectedCTC.id, data: payload })).unwrap();
        message.success('CTC updated successfully');
      } else {
        await dispatch(createCTC(payload)).unwrap();
        message.success('CTC created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      loadCTCs();
    } catch (error) {
      message.error(error || 'Failed to save CTC');
    }
  };

  const handleDeleteCTC = (ctcId) => {
    Modal.confirm({
      title: 'Delete CTC',
      content: 'Are you sure you want to delete this CTC?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await dispatch(deleteCTC(ctcId)).unwrap();
          message.success('CTC deleted successfully');
          loadCTCs();
        } catch (error) {
          message.error(error || 'Failed to delete CTC');
        }
      }
    });
  };

  const handleTableChange = (pagination) => {
    loadCTCs(pagination.current, pagination.pageSize);
  };

  const columns = [
    ...(isSuperAdmin(user) ? [{
      title: 'Organization',
      dataIndex: 'organizationId',
      key: 'organizationId',
      render: (orgId) => {
        const org = organizations.find(o => o.id === orgId);
        return org?.name || orgId;
      }
    }] : []),
    {
      title: 'Employee',
      dataIndex: ['employee', 'firstName'],
      render: (_, record) => `${record.employee?.firstName} ${record.employee?.lastName}`,
      key: 'employee'
    },
    {
      title: 'Employee Code',
      dataIndex: ['employee', 'employeeCode'],
      key: 'employeeCode'
    },
    {
      title: 'CTC Amount',
      dataIndex: 'ctcAmount',
      key: 'ctcAmount',
      render: (amount) => `₹${parseFloat(amount).toLocaleString('en-IN')}`
    },
    {
      title: 'Effect From',
      dataIndex: 'effectFrom',
      key: 'effectFrom',
      render: (date) => dayjs(date).format('DD-MMM-YYYY')
    },
    {
      title: 'Effect To',
      dataIndex: 'effectTo',
      key: 'effectTo',
      render: (date) => (date ? dayjs(date).format('DD-MMM-YYYY') : 'Ongoing')
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Created By',
      dataIndex: ['createdByUser', 'email'],
      key: 'createdBy'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewCTC(record)}
            title="View details"
          />
          {canManageCTC(user) && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditCTC(record)}
                title="Edit CTC"
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteCTC(record.id)}
                title="Delete CTC"
              />
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>CTC Management</h1>
        {canManageCTC(user) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateCTC}
          >
            Add New CTC
          </Button>
        )}
      </div>

      {isSuperAdmin(user) && (
        <div style={{ marginBottom: '20px' }}>
          <Select
            placeholder="Select Organization"
            style={{ width: 300 }}
            value={selectedOrganizationId}
            onChange={setSelectedOrganizationId}
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
        </div>
      )}

      <Spin spinning={tableLoading}>
        <Table
          columns={columns}
          dataSource={ctcs}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} CTCs`
          }}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 1200 }}
        />
      </Spin>

      {/* Create/Edit Modal */}
      <Modal
        title={isEditMode ? 'Edit CTC' : 'Add New CTC'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCTC}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select an organization' }]}
            >
              <Select
                placeholder="Select Organization"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Employee"
            name="employeeId"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              placeholder="Select Employee"
              loading={employeesLoading}
              options={employees.map((emp) => ({
                label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                value: emp.id
              }))}
            />
          </Form.Item>

          <Form.Item
            label="CTC Amount (₹)"
            name="ctcAmount"
            rules={[
              { required: true, message: 'Please enter CTC amount' },
              { pattern: /^\d+(\.\d{1,2})?$/, message: 'Please enter a valid amount' }
            ]}
          >
            <InputNumber
              min={0}
              step={1000}
              precision={2}
              placeholder="Enter CTC Amount"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="Effect From"
            name="effectFrom"
            rules={[{ required: true, message: 'Please select effect from date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>

          <Form.Item
            label="Effect To (Optional)"
            name="effectTo"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD-MMM-YYYY"
              placeholder="Leave empty for ongoing"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Drawer */}
      <Drawer
        title="CTC Details"
        placement="right"
        onClose={() => setViewDrawerOpen(false)}
        open={viewDrawerOpen}
      >
        {viewData && (
          <div>
            {isSuperAdmin(user) && (
              <p><strong>Organization:</strong> {organizations.find(o => o.id === viewData.organizationId)?.name}</p>
            )}
            <p><strong>Employee:</strong> {viewData.employee?.firstName} {viewData.employee?.lastName}</p>
            <p><strong>Employee Code:</strong> {viewData.employee?.employeeCode}</p>
            <p><strong>CTC Amount:</strong> ₹{parseFloat(viewData.ctcAmount).toLocaleString('en-IN')}</p>
            <p><strong>Effect From:</strong> {dayjs(viewData.effectFrom).format('DD-MMM-YYYY')}</p>
            <p><strong>Effect To:</strong> {viewData.effectTo ? dayjs(viewData.effectTo).format('DD-MMM-YYYY') : 'Ongoing'}</p>
            <p><strong>Status:</strong> <Tag color={viewData.isActive ? 'green' : 'red'}>{viewData.isActive ? 'Active' : 'Inactive'}</Tag></p>
            <p><strong>Created By:</strong> {viewData.createdByUser?.email}</p>
            <p><strong>Created At:</strong> {dayjs(viewData.createdAt).format('DD-MMM-YYYY HH:mm')}</p>
            <p><strong>Updated By:</strong> {viewData.updatedByUser?.email || 'N/A'}</p>
            <p><strong>Updated At:</strong> {dayjs(viewData.updatedAt).format('DD-MMM-YYYY HH:mm')}</p>
          </div>
        )}
      </Drawer>
    </Layout>
  );
}

export default CTC;
